import React, { useEffect, useState, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../App';
import { Loader2, Share2, CheckCircle, AlertCircle } from 'lucide-react';

/**
 * ShareTarget - Page qui reçoit les liens partagés depuis d'autres applications
 * 
 * Quand un utilisateur partage un lien depuis Facebook/YouTube/Instagram vers SmartBouffe,
 * cette page reçoit le lien et redirige vers le dashboard pour lancer l'extraction.
 */
export default function ShareTarget() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading } = useContext(AuthContext);
  const [status, setStatus] = useState('processing'); // processing, success, error, login_required
  const [message, setMessage] = useState('Traitement du lien partagé...');
  const [sharedUrl, setSharedUrl] = useState('');

  useEffect(() => {
    // Récupérer les paramètres partagés
    const title = searchParams.get('title') || '';
    const text = searchParams.get('text') || '';
    const url = searchParams.get('url') || '';

    console.log('📨 ShareTarget reçu:', { title, text, url });

    // Extraire l'URL du contenu partagé
    // Les apps mobiles envoient le lien différemment:
    // - Instagram: souvent dans 'text' avec du texte additionnel
    // - TikTok: parfois dans 'url', parfois dans 'text'  
    // - Facebook: souvent dans 'text' ou 'url'
    let extractedUrl = '';
    
    // Chercher une URL valide dans tous les paramètres (priorité: url > text > title)
    const urlRegex = /(https?:\/\/[^\s<>"{}|\\^`\[\]]+)/gi;
    
    for (const source of [url, text, title]) {
      if (!source) continue;
      // Si c'est déjà une URL clean
      if (source.startsWith('http://') || source.startsWith('https://')) {
        const matches = source.match(urlRegex);
        if (matches) {
          extractedUrl = matches[0];
          break;
        }
      }
      // Chercher une URL dans le texte
      const matches = source.match(urlRegex);
      if (matches && matches.length > 0) {
        // Prioritiser les URLs de plateformes vidéo connues
        const videoUrl = matches.find(m => 
          m.includes('tiktok.com') || m.includes('instagram.com') || 
          m.includes('facebook.com') || m.includes('youtube.com') || 
          m.includes('youtu.be') || m.includes('fb.watch')
        );
        extractedUrl = videoUrl || matches[0];
        break;
      }
    }

    // Nettoyer l'URL
    if (extractedUrl) {
      // Retirer les caractères de fin indésirables
      extractedUrl = extractedUrl.replace(/[)\]}>.,;!?]+$/, '');
      
      try {
        const urlObj = new URL(extractedUrl);
        // YouTube: normaliser
        if (urlObj.hostname.includes('youtu.be')) {
          const videoId = urlObj.pathname.split('/').pop();
          if (videoId) extractedUrl = `https://www.youtube.com/watch?v=${videoId}`;
        }
      } catch (e) {
        // URL parsing non critique
      }
    }

    setSharedUrl(extractedUrl);

    // Attendre que l'auth soit chargée
    if (authLoading) {
      return;
    }

    // Vérifier si l'utilisateur est connecté
    if (!user) {
      setStatus('login_required');
      setMessage('Connectez-vous pour extraire cette recette');
      // Stocker l'URL pour après la connexion
      if (extractedUrl) {
        localStorage.setItem('pendingShareUrl', extractedUrl);
      }
      return;
    }

    // Si on a une URL valide, rediriger vers le dashboard
    if (extractedUrl) {
      setStatus('success');
      setMessage('Redirection vers SmartBouffe...');
      
      // Rediriger vers le dashboard avec l'URL en paramètre
      setTimeout(() => {
        navigate(`/dashboard?share=${encodeURIComponent(extractedUrl)}`);
      }, 1000);
    } else {
      setStatus('error');
      setMessage('Aucun lien valide trouvé dans le partage');
    }
  }, [searchParams, user, authLoading, navigate]);

  const handleLogin = () => {
    navigate('/login');
  };

  const handleRetry = () => {
    navigate('/dashboard');
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #F5F1E8 0%, #E8E4D9 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1rem'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '20px',
        padding: '2rem',
        maxWidth: '400px',
        width: '100%',
        textAlign: 'center',
        boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
      }}>
        {/* Logo */}
        <div style={{ marginBottom: '1.5rem' }}>
          <Share2 size={48} color="#87A96B" />
        </div>

        {/* Status Icon */}
        <div style={{ marginBottom: '1rem' }}>
          {status === 'processing' && (
            <Loader2 size={40} className="animate-spin" style={{ color: '#87A96B' }} />
          )}
          {status === 'success' && (
            <CheckCircle size={40} style={{ color: '#87A96B' }} />
          )}
          {(status === 'error' || status === 'login_required') && (
            <AlertCircle size={40} style={{ color: '#E57373' }} />
          )}
        </div>

        {/* Message */}
        <h2 style={{
          fontSize: '1.25rem',
          color: '#3A4A3E',
          marginBottom: '0.5rem',
          fontWeight: '600'
        }}>
          {status === 'processing' && 'Traitement en cours...'}
          {status === 'success' && 'Lien reçu !'}
          {status === 'error' && 'Oops !'}
          {status === 'login_required' && 'Connexion requise'}
        </h2>

        <p style={{
          color: '#666',
          marginBottom: '1.5rem',
          fontSize: '0.95rem'
        }}>
          {message}
        </p>

        {/* URL partagée */}
        {sharedUrl && (
          <div style={{
            background: '#F5F1E8',
            borderRadius: '10px',
            padding: '0.75rem',
            marginBottom: '1.5rem',
            wordBreak: 'break-all',
            fontSize: '0.8rem',
            color: '#5D6F61'
          }}>
            {sharedUrl.length > 60 ? sharedUrl.substring(0, 60) + '...' : sharedUrl}
          </div>
        )}

        {/* Actions */}
        {status === 'login_required' && (
          <button
            onClick={handleLogin}
            style={{
              width: '100%',
              padding: '0.875rem',
              background: '#87A96B',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Se connecter
          </button>
        )}

        {status === 'error' && (
          <button
            onClick={handleRetry}
            style={{
              width: '100%',
              padding: '0.875rem',
              background: '#87A96B',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Aller au dashboard
          </button>
        )}
      </div>
    </div>
  );
}
